import React, { useState, useEffect } from 'react'
import Header from './Header.jsx'
import Navigator from './Navigator.jsx'
import { Link } from 'react-router-dom'
import { useParams } from 'react-router-dom';
import EventCard from './EventCard.jsx';
import "../css/EventDetails.css"
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import useApiRequest from './useApiRequest.jsx';
import { useLoadingContext } from './LoadingContext.jsx';
import { domain } from './Domain.jsx';
import {motion, AnimatePresence} from "framer-motion"
import EventDetailsSlider from './EventDetailsSlider.jsx';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Calendar, MapPin, Clock, Users, Share2, Heart, Eye, Star, ArrowRight, Phone, Mail, Globe, Ticket, CheckCircle } from 'lucide-react';
// import Swiper and modules styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const CategorySelect = ({ ticketDetails, eventDetails }) => {
    // Function to format the date to 'August 3, 2023'
    function formatDate(date) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    }

    return (
        <div className="space-y-6">
            <div className="text-center">
                <p className="text-orange-500 font-semibold text-lg">
                    {eventDetails && formatDate(new Date(eventDetails.start_date_event))}
                </p>
                <h3 className="text-2xl font-bold text-gray-800 mt-2">
                    Select Your Ticket Type
                </h3>
            </div>
            
            <div className="space-y-4">
                {ticketDetails && ticketDetails.map(item => (
                    <motion.div 
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200"
                    >
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                            <div className="flex-1">
                                <h4 className="text-lg font-semibold text-gray-800 mb-2">
                                    {item.ticket_category.name}
                                </h4>
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                    <span className="text-2xl font-bold text-orange-500">
                                        Ugx {item.ticket_price.toLocaleString()}
                                    </span>
                                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                                        {item.tickets_available.toLocaleString()} Available
                                    </span>
                                </div>
                            </div>
                            <div className="bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2">
                                Preview Mode
                                <Eye className="w-4 h-4" />
                            </div>
                </div>
                    </motion.div>
                ))}
            </div>
        </div>
    )
}

const EventPreview = ({ completed, regData, handleApprove }) => {
    const { loading, setLoading } = useLoadingContext()
    const { getRequest } = useApiRequest()
    const [eventDetails, setEventDetails] = useState(null)
    const [ticketDetails, setTicketDetails] = useState(null)
    const [cheapestTicket, setCheapestTicket] = useState(null);
    const [highestTicket, setHighestTicket] = useState(null);

    const filterTickets = (tickets) => {
        if (!tickets || tickets.length === 0) return;

        const cheapest = tickets.reduce((prev, curr) => {
            return (prev.ticket_price < curr.ticket_price) ? prev : curr;
        });

        const highest = tickets.reduce((prev, curr) => {
            return (prev.ticket_price > curr.ticket_price) ? prev : curr;
        });

        setCheapestTicket(cheapest);
        setHighestTicket(highest);
    };

    const fetchTicketDetails = async () => {
        const details = await getRequest(`${domain}/get-tickets?slug=${regData}`)
        setTicketDetails(details.body)
        filterTickets(details.body);
    }

    const fetchEventDetails = async () => {
        const details = await getRequest(`${domain}/event-details/${regData}`)
        setEventDetails(details.body.details)
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            if (!regData) {
                setLoading(false);
                return;
            }

            try {
                // Perform all fetch requests in parallel and wait for all of them to complete
                await Promise.all([fetchEventDetails(), fetchTicketDetails()]);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                // Set loading to false when all requests are completed (or error occurs)
                setLoading(false);
            }
        };

        fetchData();
    }, [regData])

    // Function to format the date to 'August 3, 2023'
    function formatDate(date) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    }

    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const truncateString = (str) => {
        return str && str.length > 90 ? str.substring(0, 89) + "..." : str;
    }

    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Function to handle scroll event
        const handleScroll = () => {
            // Check if the user has scrolled more than 100px from the top
            if (window.scrollY > 100) {
                setIsVisible(true);
            } else {
                setIsVisible(false);
            }
        };

        // Attach the event listener for scrolling
        window.addEventListener('scroll', handleScroll);

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Generate shareable URLs
    const shareUrl = window.location.href;
    const shareTitle = eventDetails?.event_plot_name || 'Check out this event!';
    const shareDescription = truncateString(eventDetails?.event_description) || 'Join us for an exciting event!';
    const whatsappShare = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareTitle} - ${shareDescription} ${shareUrl}`)}`;
    const facebookShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareTitle)}`;
    const twitterShare = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${shareTitle} - ${shareDescription}`)}&url=${encodeURIComponent(shareUrl)}`;
    const instagramMessage = "To share on Instagram, copy the event link and paste it in your Instagram post or story!";

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {eventDetails && (
                <>
                    {/* Hero Section */}
                    <div className="relative">
                        <div className="relative h-96 overflow-hidden">
                            <img 
                                className="w-full h-full object-cover" 
                                src={domain + eventDetails.banner} 
                                alt={eventDetails.event_plot_name}
                            />
                            <div className="absolute inset-0 bg-black/20"></div>
                            
                            {/* Preview Badge */}
                            <div className="absolute top-4 left-4">
                                <div className="bg-orange-500 text-white px-4 py-2 rounded-full font-semibold flex items-center gap-2">
                                    <Eye className="w-4 h-4" />
                                    Preview Mode
                                </div>
                            </div>
                            
                            {/* Event Info Overlay */}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent p-8">
                                <div className="container mx-auto">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                            {eventDetails.event_category.name}
                                        </span>
                                    </div>
                                    <h1 className="text-4xl font-bold text-white mb-2">
                                        {eventDetails.event_plot_name}
                                    </h1>
                                    <p className="text-gray-200 text-lg">
                                        By {eventDetails.organiser.organiser_name}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="container mx-auto px-4 py-8">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Left Column - Event Details */}
                            <div className="lg:col-span-2 space-y-8">
                                {/* About Section */}
                                <motion.div 
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-white rounded-2xl shadow-lg p-8"
                                >
                                    <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                                        <Star className="w-6 h-6 text-orange-500" />
                                        About This Event
                                    </h2>
                                    <p className="text-gray-700 leading-relaxed text-lg">
                                        {eventDetails.event_description}
                                    </p>
                                </motion.div>

                                {/* Event Details */}
                                <motion.div 
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 }}
                                    className="bg-white rounded-2xl shadow-lg p-8"
                                >
                                    <h2 className="text-2xl font-bold text-gray-800 mb-6">Event Details</h2>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* Date and Time */}
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                                <Calendar className="w-5 h-5 text-orange-500" />
                                                Date and Time
                                            </h3>
                                            <div className="space-y-2 text-gray-600">
                                                <p><strong>Start:</strong> {formatDate(new Date(eventDetails.start_date_event))}</p>
                                                <p><strong>End:</strong> {formatDate(new Date(eventDetails.end_date_event))}</p>
                                            </div>
                                        </div>

                                        {/* Location */}
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                                <MapPin className="w-5 h-5 text-teal-500" />
                                                Location
                                            </h3>
                                            <div className="space-y-2 text-gray-600">
                                                <p><strong>Venue:</strong> {eventDetails.venue_name}</p>
                                                <p><strong>Address:</strong> {eventDetails.venue_address}</p>
                                            </div>
                                        </div>
                                </div>

                                    {/* Additional Details */}
                                    <div className="mt-8 space-y-6">
                                <div>
                                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Age Restriction</h3>
                                            <p className="text-gray-600">{eventDetails.age_restriction}</p>
                                        </div>
                                        
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Refund Policy</h3>
                                            <p className="text-gray-600">{eventDetails.refund_policy}</p>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Map */}
                                <motion.div 
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="bg-white rounded-2xl shadow-lg overflow-hidden"
                                >
                                    <div className="p-6 border-b border-gray-100">
                                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                            <MapPin className="w-5 h-5 text-teal-500" />
                                            Event Location
                                        </h3>
                                    </div>
                                    <iframe
                                        width="100%"
                                        height="300"
                                        frameBorder="0"
                                        style={{ border: 0 }}
                                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBGY-aKP5KQbjJbf2B2mN6u9909uI5iv50&q=${eventDetails.venue_address}`}
                                        allowFullScreen
                                    />
                                </motion.div>

                                {/* Share Section */}
                                <motion.div 
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="bg-white rounded-2xl shadow-lg p-8"
                                >
                                    <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                                        <Share2 className="w-5 h-5 text-orange-500" />
                                        Share This Event
                                    </h3>
                                    <div className="flex gap-4">
                                        <motion.a 
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            href={whatsappShare} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="w-12 h-12 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center text-white transition-colors duration-200"
                                        >
                                            <i className="fab fa-whatsapp text-xl"></i>
                                        </motion.a>
                                        <motion.a 
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            href={facebookShare} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="w-12 h-12 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center text-white transition-colors duration-200"
                                        >
                                            <i className="fab fa-facebook text-xl"></i>
                                        </motion.a>
                                        <motion.a 
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            href="#" 
                                            onClick={() => alert(instagramMessage)}
                                            className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-full flex items-center justify-center text-white transition-colors duration-200"
                                        >
                                            <i className="fab fa-instagram text-xl"></i>
                                        </motion.a>
                                        <motion.a 
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            href={twitterShare} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="w-12 h-12 bg-sky-500 hover:bg-sky-600 rounded-full flex items-center justify-center text-white transition-colors duration-200"
                                        >
                                            <i className="fab fa-twitter text-xl"></i>
                                        </motion.a>
                                </div>
                                </motion.div>
                            </div>

                            {/* Right Column - Ticket Info */}
                            <div className="lg:col-span-1">
                                <motion.div 
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="sticky top-8"
                                >
                                    <div className="bg-white rounded-2xl shadow-lg p-6 space-y-6">
                                        {/* Price Range */}
                                        <div className="text-center">
                                            <h3 className="text-lg font-semibold text-gray-700 mb-2">Tickets Available From</h3>
                                            <div className="text-3xl font-bold text-orange-500">
                                                Ugx {cheapestTicket && cheapestTicket.ticket_price.toLocaleString()}
                                            </div>
                                            {highestTicket && (
                                                <div className="text-sm text-gray-500 mt-1">
                                                    to Ugx {highestTicket.ticket_price.toLocaleString()}
                                                </div>
                                            )}
                                        </div>

                                        <hr className="border-gray-200" />

                                        {/* Event Info */}
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-3 text-gray-600">
                                                <Calendar className="w-5 h-5 text-orange-500" />
                                                <span>{formatDate(new Date(eventDetails.start_date_event))}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-gray-600">
                                                <MapPin className="w-5 h-5 text-teal-500" />
                                                <span>{eventDetails.venue_name}</span>
                                            </div>
                                </div>

                                        {/* Preview Button */}
                                        <motion.button 
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleShow}
                                            className="w-full bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 mb-4"
                                        >
                                            <Eye className="w-5 h-5" />
                                            Preview Tickets
                                        </motion.button>

                                        {/* Event Image */}
                                        <div className="rounded-xl overflow-hidden">
                                            <img
                                                className="w-full h-48 object-cover"
                                                src={domain + eventDetails.listing_img}
                                                alt={eventDetails.event_plot_name}
                                            />
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Ticket Preview Modal */}
                    <Modal
                        show={show}
                        onHide={handleClose}
                        size="lg"
                        aria-labelledby="contained-modal-title-vcenter"
                        centered
                    >
                <Modal.Header closeButton className="border-b-0 pb-0">
                    <Modal.Title className="text-2xl font-bold text-gray-800">
                        {eventDetails && eventDetails.event_plot_name}
                    </Modal.Title>
                        </Modal.Header>
                <Modal.Body className="pt-4">
                    <CategorySelect ticketDetails={ticketDetails} eventDetails={eventDetails} />
                        </Modal.Body>
                    </Modal>

            {/* Floating Publish Button */}
                            <AnimatePresence>
                {isVisible && regData && (
                                    <motion.div
                                        initial={{ opacity: 0, x: 100 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 100 }}
                                        transition={{ duration: 0.3 }}
                        className="fixed bottom-8 right-8 z-50"
                                        >
                                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                            disabled={completed} 
                            onClick={handleApprove}
                            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-4 rounded-full font-semibold shadow-2xl hover:shadow-3xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <CheckCircle className="w-5 h-5" />
                            Publish Event
                        </motion.button>
                                    </motion.div>
                )}
                            </AnimatePresence>
                        </div>
    )
}

export default EventPreview